/**
 * When a persisted vibe thread may be silently resumed.
 *
 * History is kept for days so the renter can scroll back, but *auto-resuming*
 * a cold thread is not the same thing: VibeSearchPage only creates a session
 * when `activeSessionId` is null, so a stale id silently reopens the server-side
 * session too — and the backend keeps applying that session's `last_criteria`
 * to whatever the renter types next. One renter hit this and wrote "I thought
 * this was a new search" while a stale $1,500 budget kept zeroing their results.
 *
 * Inside the window, returning from a tab switch or a property detour still
 * continues the conversation, which is what people expect.
 *
 * Kept in its own module so the rule is unit-testable without dragging in the
 * store's whole dependency graph.
 */

/** How long an idle thread stays auto-resumable. */
export const RESUME_MAX_IDLE_MS = 2 * 60 * 60 * 1000;

/** Minimal shape needed to make the decision. */
export interface ResumableSession {
  updatedAt?: number;
}

/**
 * The session id to reopen on load, or null to start a fresh thread.
 * Null-safe on purpose: a missing/zero `updatedAt` reads as cold, because
 * inheriting criteria we can't date is the failure we're preventing.
 */
export function resolveResumableSessionId<T extends ResumableSession>(
  sessions: Record<string, T>,
  activeSessionId: string | null,
  now: number = Date.now(),
): string | null {
  if (!activeSessionId) return null;
  const session = sessions[activeSessionId];
  if (!session) return null;
  const updatedAt = session.updatedAt ?? 0;
  if (!updatedAt) return null;
  return now - updatedAt <= RESUME_MAX_IDLE_MS ? activeSessionId : null;
}
